export const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/internal';

let authToken: string | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Low-level fetch that returns the raw JSON body (no unwrapping).
 */
export async function rawFetchJson<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (authToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${authToken}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, body.error || `Request failed: ${response.status}`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * Low-level fetch for FormData (multipart) — does NOT set Content-Type so
 * the browser can add the correct multipart boundary.
 */
export async function rawFetchFormData<T>(
  endpoint: string,
  body: FormData,
): Promise<T> {
  const headers: Record<string, string> = {};

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers,
    body,
  });

  if (!response.ok) {
    const json = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, json.error || `Request failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch JSON and auto-unwrap the `{ data: ... }` envelope used by sendSuccess().
 */
export async function fetchJson<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const json = await rawFetchJson<{ data: T } | T>(endpoint, options);
  if (json && typeof json === 'object' && 'data' in json) {
    return (json as { data: T }).data;
  }
  return json as T;
}
